import { useEffect } from 'react';
import type { Editor } from '@tiptap/react';

import { RENDERED_BAND_SELECTOR } from 'containers/Tiptap/lib/header-footer-dom';

/**
 * Closes the Pages header/footer overlay when the user clicks anywhere outside
 * the overlay element. The Pages extension itself handles Escape and double-click
 * outside; this covers single-click outside (e.g. clicking back into the body).
 */
export function useCloseOverlayOnOutsideClick(editor: Editor | null): void {
  useEffect(() => {
    if (!editor) return undefined;

    const handleMouseDown = (e: MouseEvent): void => {
      const storage = (editor.storage as { pages?: { activeEditor?: unknown } }).pages;
      if (!storage?.activeEditor) return;

      const target = e.target as HTMLElement;
      // Keep overlay open when clicking inside it, in the toolbar, or in any
      // MUI popover / menu rendered in a portal (role="menu", role="listbox",
      // role="tooltip", and the MUI Popper container). A rendered band is spared too:
      // its mousedown is the first half of the double-click that switches header ↔ footer.
      if (
        target.closest('.tiptap-header-editor-overlay') ||
        target.closest(RENDERED_BAND_SELECTOR) ||
        target.closest('.tiptap-footer-editor-overlay') ||
        target.closest('[data-tiptap-toolbar]') ||
        target.closest('[role="menu"]') ||
        target.closest('[role="listbox"]') ||
        target.closest('[role="tooltip"]') ||
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiPopper-root')
      )
        return;

      const cmds = editor.commands as Record<string, unknown>;
      if (typeof cmds.closeHeaderFooterEditors === 'function') {
        editor.commands.closeHeaderFooterEditors();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [editor]);
}
